/**
 * Converts keybinding strings into human-readable display key arrays
 * suitable for rendering in the keyboard shortcuts modal.
 *
 * Supports two display styles:
 * - `'symbols'` (default): Unicode modifier symbols (⌘, ⇧, ⌃, ⌥)
 * - `'text'`: Full key names (Cmd, Shift, Ctrl, Opt)
 *
 * @example
 * ```ts
 * keybindingToDisplayKeys('cmd+k')             // → ['⌘', 'K']
 * keybindingToDisplayKeys('cmd+k', 'text')     // → ['Cmd', 'K']
 * keybindingToDisplayKeys('ctrl+;')            // → ['⌃', ';']
 * keybindingToDisplayKeys('ctrl+;', 'text')    // → ['Ctrl', ';']
 * keybindingToDisplayKeys('escape')            // → ['Esc']
 * ```
 */
const MODIFIER_DISPLAY_SYMBOLS = {
    cmd: '⌘',
    meta: '⌘',
    ctrl: '⌃',
    control: '⌃',
    shift: '⇧',
    alt: '⌥',
    option: '⌥',
};
const MODIFIER_DISPLAY_TEXT = {
    cmd: 'Cmd',
    meta: 'Cmd',
    ctrl: 'Ctrl',
    control: 'Ctrl',
    shift: 'Shift',
    alt: 'Alt',
    option: 'Opt',
};
const SPECIAL_KEY_DISPLAY = {
    escape: 'Esc',
    esc: 'Esc',
    enter: 'Enter',
    return: 'Enter',
    backspace: 'Backspace',
    delete: 'Del',
    tab: 'Tab',
    space: 'Space',
    arrowup: '↑',
    arrowdown: '↓',
    arrowleft: '←',
    arrowright: '→',
};
/**
 * Converts a single keybinding string (e.g. `"cmd+k"`) into an array
 * of display-friendly key labels (e.g. `["⌘", "K"]`).
 *
 * @param style - `'symbols'` for Unicode symbols, `'text'` for full key names.
 */
function keybindingToDisplayKeys(keybinding, style = 'symbols') {
    const modifierMap = style === 'text' ? MODIFIER_DISPLAY_TEXT : MODIFIER_DISPLAY_SYMBOLS;
    const tokens = keybinding
        .toLowerCase()
        .split('+')
        .map((t) => t.trim())
        .filter(Boolean);
    return tokens.map((token) => {
        const modifier = modifierMap[token];
        if (modifier)
            return modifier;
        const special = SPECIAL_KEY_DISPLAY[token];
        if (special)
            return special;
        // Single-character keys are uppercased; multi-character keys preserved.
        return token.length === 1 ? token.toUpperCase() : token;
    });
}
/**
 * Converts a keybinding array into display keys. Returns the display keys
 * of the **first** keybinding — typically the modifier-based primary combo
 * (e.g. `Cmd+J`) rather than bare-key alternatives (e.g. `/`).
 */
export function resolveDisplayKeys(keybinding, style = 'symbols') {
    const primary = keybinding[0];
    return primary ? keybindingToDisplayKeys(primary, style) : [];
}
/**
 * Joins an array of display keys into a single inline string.
 *
 * - In `'symbols'` mode keys are concatenated without a separator (e.g. `⌘K`).
 * - In `'text'` mode keys are joined with `+` (e.g. `Cmd+K`).
 */
export function joinDisplayKeys(keys, style = 'symbols') {
    return keys.join(style === 'text' ? '+' : '');
}
//# sourceMappingURL=keybindingDisplay.js.map